import { Button } from 'components/Button';
import { Grid, GridProps } from 'components/Grid/Grid';
import { ICONS } from 'components/Icon';
import { Page } from 'components/Page';
import { PageEmpty } from 'components/PageEmpty';
import { Txt } from 'components/Txt';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { openUrl, rbAddReferencePrefix } from 'lib/riskbase';
import { theme } from 'lib/theme';
import * as React from 'react';
import { StyleSheet, View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  OPEN_BARCODE_VIEWER,
  doReportingGetReportListData,
} from 'store/actions';
import {
  selectRptReportItem,
  selectRptReportLoaded,
  selectRptReportOrder,
  selectRptReportTotalCount,
} from 'store/remote/selectors';
import { ReportEnum, ReportTypes } from 'types/Reports';
import QRCode from 'react-native-qrcode-svg';
import { Pressable } from 'components/Pressable';
import { IconButton } from 'components/IconButton';
import { smartText } from 'lib/str';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { ReportingGridLoader } from '../../ReportingGridLoader';
import { Header } from './Header';
import { Visuals } from './Visuals';

type Report = ReportTypes[ReportEnum.elements];
type DefinedGridProps = GridProps<
  Report['columns'],
  string,
  { reportKey: string }
>;

const stylesGlobal = StyleSheet.create({
  chip: {
    borderRadius: 6,
    paddingHorizontal: 3,
  },
});

const useGetRowData: DefinedGridProps['getRowData'] = ({ id, extraData }) => {
  const dispatch = useDispatch();
  const data = useSelector((state) =>
    selectRptReportItem(state, extraData.reportKey, id),
  ) as undefined | Report['structure'];

  return React.useMemo(() => {
    if (!data) {
      return null;
    }
    return {
      onPress: () => {
        // console.log('Press');
      },
      columns: {
        element: () => {
          const { qr, engageUrl } = data.element;
          return (
            <View
              style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}
            >
              <View style={{ flex: 1 }}>
                <Txt numberOfLines={1} weight="bold">
                  {data.element.typeSingular}
                </Txt>
                <Txt size="xsmall" numberOfLines={1}>
                  {rbAddReferencePrefix(data.element.reference)}
                  {data.element.customReference
                    ? ` • ${data.element.customReference}`
                    : ''}
                </Txt>
              </View>
              <ViewWithSeparators
                horizontal
                separatorMargin={theme.paddingHorizontalHalf / 2}
                separatorColor="transparent"
                style={{ alignItems: 'center' }}
              >
                {!!engageUrl && (
                  <IconButton
                    icon={ICONS.ArrowUpRightFromSquareLight}
                    onPress={() => openUrl(engageUrl)}
                    iconSize={12}
                    accessibilityLabel="Open engage website for this element"
                    accessibilityHint={smartText(
                      "Opens this element's engage website",
                    )}
                  />
                )}
                {!!qr && (
                  <Pressable
                    style={{
                      width: 42,
                      height: 42,
                      marginTop: -6,
                      marginBottom: -6,
                    }}
                    onPress={() =>
                      dispatch(
                        OPEN_BARCODE_VIEWER({
                          data: qr,
                        }),
                      )
                    }
                  >
                    <QRCode
                      value={qr}
                      size={42}
                      backgroundColor="transparent"
                      color={
                        qr === engageUrl
                          ? theme.colors.green
                          : theme.colors.black
                      }
                    />
                  </Pressable>
                )}
              </ViewWithSeparators>
            </View>
          );
        },
        elementLocation: (
          <View style={{ flex: 1 }}>
            <Txt numberOfLines={3} lineHeightNone>
              {data.element.locationInfo.map((location, index) => (
                <React.Fragment key={index}>
                  {index > 0 ? ', ' : ''}
                  <Txt weight="bold" size="xsmall" lineHeightNone>
                    {location.label}:{' '}
                  </Txt>
                  <Txt size="xsmall" lineHeightNone>
                    {location.value}
                  </Txt>
                </React.Fragment>
              ))}
            </Txt>
          </View>
        ),
        asset: (
          <View style={{ flex: 1 }}>
            <Txt numberOfLines={1} weight="bold">
              {data.asset.name}
            </Txt>
            <Txt size="xsmall" numberOfLines={1}>
              {rbAddReferencePrefix(data.asset.reference)}
              {data.asset.customReference
                ? ` • ${data.asset.customReference}`
                : ''}
            </Txt>
          </View>
        ),
        tasks: (
          <View
            style={{
              flex: 1,
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 3,
              alignItems: 'center',
            }}
          >
            {!data.tasks.notstarted.count &&
              !data.tasks.inprogress.count &&
              !data.tasks.completed.count && (
                <View
                  style={[
                    stylesGlobal.chip,
                    {
                      backgroundColor: '#fafafa',
                    },
                  ]}
                >
                  <Txt
                    size="xsmall"
                    style={{
                      color: theme.colors.placeholder,
                    }}
                  >
                    No Tasks
                  </Txt>
                </View>
              )}
            {!!data.tasks.notstarted.count && (
              <View
                style={[
                  stylesGlobal.chip,
                  {
                    backgroundColor: `#${data.tasks.notstarted.color}`,
                  },
                ]}
              >
                <Txt
                  size="xsmall"
                  style={{
                    color: theme.colors.white,
                  }}
                >
                  {data.tasks.notstarted.count} {data.tasks.notstarted.name}
                </Txt>
              </View>
            )}
            {!!data.tasks.inprogress.count && (
              <View
                style={[
                  stylesGlobal.chip,
                  {
                    backgroundColor: `#${data.tasks.inprogress.color}`,
                  },
                ]}
              >
                <Txt
                  size="xsmall"
                  style={{
                    color: theme.colors.white,
                  }}
                >
                  {data.tasks.inprogress.count} {data.tasks.inprogress.name}
                </Txt>
              </View>
            )}
            {!!data.tasks.completed.count && (
              <View
                style={[
                  stylesGlobal.chip,
                  {
                    backgroundColor: `#${data.tasks.completed.color}`,
                  },
                ]}
              >
                <Txt
                  size="xsmall"
                  style={{
                    color: theme.colors.white,
                  }}
                >
                  {data.tasks.completed.count} {data.tasks.completed.name}
                </Txt>
              </View>
            )}
          </View>
        ),
        checks: () => {
          const filteredChecks = data.checks.filter((c) => !!c.count);
          return (
            <View
              style={{
                flex: 1,
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 3,
                alignItems: 'center',
              }}
            >
              {filteredChecks.length ? (
                filteredChecks.map((item) => (
                  <View
                    key={item.key}
                    style={[
                      stylesGlobal.chip,
                      {
                        backgroundColor: `#${item.color}`,
                      },
                    ]}
                  >
                    <Txt
                      size="xsmall"
                      style={{
                        color: theme.colors.white,
                      }}
                    >
                      {item.count} {item.name}
                    </Txt>
                  </View>
                ))
              ) : (
                <View
                  style={[
                    stylesGlobal.chip,
                    {
                      backgroundColor: '#fafafa',
                    },
                  ]}
                >
                  <Txt
                    size="xsmall"
                    style={{
                      color: theme.colors.placeholder,
                    }}
                  >
                    No Checks
                  </Txt>
                </View>
              )}
            </View>
          );
        },
        checkSchedules: () => {
          const filteredChecks = data.checkSchedules.filter((c) => !!c.count);
          return (
            <View
              style={{
                flex: 1,
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 3,
                alignItems: 'center',
              }}
            >
              {filteredChecks.length ? (
                filteredChecks.map((item) => (
                  <View
                    key={item.key}
                    style={[
                      stylesGlobal.chip,
                      {
                        backgroundColor: `#${item.color}`,
                      },
                    ]}
                  >
                    <Txt
                      size="xsmall"
                      style={{
                        color: theme.colors.white,
                      }}
                    >
                      {item.count} {item.name}
                    </Txt>
                  </View>
                ))
              ) : (
                <View
                  style={[
                    stylesGlobal.chip,
                    {
                      backgroundColor: '#fafafa',
                    },
                  ]}
                >
                  <Txt
                    size="xsmall"
                    style={{
                      color: theme.colors.placeholder,
                    }}
                  >
                    No Check Schedules
                  </Txt>
                </View>
              )}
            </View>
          );
        },
      },
    };
  }, [data, dispatch]);
};

export const Main: React.FC<{ reportKey: string }> = ({ reportKey }) => {
  const dispatch = useDispatch();
  const [columns] = React.useState<DefinedGridProps['columns']>([
    {
      name: 'Element',
      dataIndex: 'element',
      width: 300,
    },
    {
      name: 'Element Location',
      dataIndex: 'elementLocation',
      width: 300,
    },
    {
      name: 'Asset',
      dataIndex: 'asset',
      width: 200,
    },
    {
      name: 'Tasks',
      dataIndex: 'tasks',
      width: 200,
    },
    {
      name: 'Checks',
      dataIndex: 'checks',
      width: 200,
    },
    {
      name: 'Check Schedules',
      dataIndex: 'checkSchedules',
      width: 200,
    },
  ]);

  const reportLoaded = useSelector((state) =>
    selectRptReportLoaded(state, reportKey),
  );
  const reportOrder = useSelector((state) =>
    selectRptReportOrder(state, reportKey),
  );
  const reportTotalCount = useSelector((state) =>
    selectRptReportTotalCount(state, reportKey),
  );
  const serverCount = reportTotalCount - reportOrder.length;

  const handleLoadReportData = React.useCallback(() => {
    dispatch(
      doReportingGetReportListData({
        key: reportKey,
        nextPage: true,
        closeModals: false,
        overlayLoader: false,
      }),
    );
  }, [dispatch, reportKey]);

  return (
    <Page
      isInModal={false}
      contentPaddingTop={0}
      ignoreSafeAreaBottom
      header={<Header reportKey={reportKey} />}
    >
      <Visuals reportKey={reportKey} />
      <View style={{ flex: 1 }}>
        <ReportingGridLoader reportKey={reportKey} />
        {!reportLoaded ? (
          <PageEmpty
            text="Waiting..."
            subtext="Press load to get started"
            iconCircled={false}
            style={{ backgroundColor: theme.colors.background }}
          />
        ) : !reportOrder.length ? (
          <PageEmpty
            text="No Results Found"
            subtext="Try changing your filter"
            iconCircled={false}
            style={{ backgroundColor: theme.colors.background }}
          />
        ) : (
          <Grid
            columns={columns}
            data={reportOrder}
            extraData={{ reportKey }}
            getRowData={useGetRowData}
            keyExtractor={(i) => `${i}`}
            ListFooterComponent={
              serverCount > 0 ? (
                <View
                  style={{
                    padding: theme.paddingHorizontal * 2,
                    paddingTop: theme.paddingHorizontal,
                    alignItems: 'flex-start',
                  }}
                >
                  <Button
                    text={pluralise(
                      serverCount,
                      '{x} More Result, Load',
                      '{x} More Results, Load More',
                    )}
                    onPress={handleLoadReportData}
                    icon={ICONS.ArrowDownLight}
                  />
                </View>
              ) : (
                <View style={{ height: theme.paddingHorizontal * 2 }} />
              )
            }
            // refreshControl,
          />
        )}
      </View>
    </Page>
  );
};
